/**
 * The box a note is written in.
 *
 * Four things: bold, a bulleted list, a highlight, and a colour. That is the
 * whole vocabulary, and it is the whole vocabulary because a note is not a
 * document — what people want is to make one word bold, list three things, and
 * mark the line that matters so nobody misses it at seven in the morning.
 *
 * `contentEditable` with `document.execCommand`, which is deprecated and still
 * the only thing every browser implements for this. The alternative is a
 * selection-model editor of our own, which is a month of work and a class of
 * bug — cursor jumps, lost selections, broken undo — that nobody would thank
 * us for. What execCommand produces is untrusted either way: everything is run
 * through `sanitizeNote` on the way out, and again on the server.
 *
 * The colours are token classes rather than inline styles, for the reason
 * given in `shared/rich-text.ts`: a note written in #ff00ff is a note nobody
 * can read in dark mode.
 *
 * The toolbar wears the glyphs everybody has already pressed a thousand times
 * somewhere else — a bold B, a slanted I, a bulleted list, a highlighter pen,
 * and an A sitting on a bar in the colour it would write in. They were letters
 * typed in a bold or highlighted span, which read as content rather than as
 * controls: an "H" in a yellow box is not a button anybody recognises.
 */
import { useEffect, useRef, useState } from 'react'
import { NOTE_COLORS, NOTE_MARK_CLASS, noteColorClass, sanitizeNote } from '@rowted/shared'
import { MenuSelect } from './menu-select.js'
import {
  IconBold, IconBulletList, IconHighlight, IconItalic, IconTextColor,
} from '../frame/icons.js'
import { cn } from '../lib/cn.js'

export interface NoteEditorProps {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  disabled?: boolean
  /** Rows of room before it scrolls. */
  minHeight?: number
}

export function NoteEditor({
  value, onChange, placeholder, disabled, minHeight = 64,
}: NoteEditorProps) {
  const box = useRef<HTMLDivElement>(null)
  const [empty, setEmpty] = useState(() => value.trim() === '')

  /*
   * Written into the element rather than bound to it. A `contentEditable`
   * whose innerHTML React controls loses the caret on every keystroke, so the
   * value flows out on input and back in only when it changed underneath —
   * the note being cleared after it is saved, mostly.
   */
  useEffect(() => {
    const node = box.current
    if (!node || node.innerHTML === value) return
    /*
     * An empty value is the one write that happens through a live caret.
     *
     * The parent sends '' only when it has deliberately cleared the box —
     * after a note is posted, which is the case this write-back exists for.
     * The editor previously refused it whenever the box had focus, so a note
     * stayed on screen after it was sent and the next one was typed on top of
     * it. Every other incoming value is still ignored while somebody is
     * typing, because controlling innerHTML under a caret loses the caret.
     */
    if (document.activeElement === node && value !== '') return
    node.innerHTML = value
    setEmpty(node.textContent?.trim() === '')
  }, [value])

  const emit = () => {
    const node = box.current
    if (!node) return
    setEmpty(node.textContent?.trim() === '')
    onChange(sanitizeNote(node.innerHTML))
  }

  /** Run a command against the selection and report what came out. */
  const run = (command: string, argument?: string) => {
    box.current?.focus()
    document.execCommand(command, false, argument)
    emit()
  }

  /**
   * Wrap the selection in one of our classes.
   *
   * `execCommand('foreColor')` writes `<font color>` or an inline style, both
   * of which the sanitiser drops — rightly. So the colour and the highlight
   * are applied by hand: take the selected range, put it in a span with the
   * class, and put it back.
   */
  const wrap = (className: string, toggleOff: (element: HTMLElement) => boolean) => {
    const node = box.current
    if (!node) return
    node.focus()
    const selection = window.getSelection()
    if (!selection || selection.rangeCount === 0 || selection.isCollapsed) return

    const range = selection.getRangeAt(0)
    if (!node.contains(range.commonAncestorContainer)) return

    // Already wrapped in this exact thing → take it off instead.
    const parent = range.commonAncestorContainer.parentElement
    if (parent && parent !== node && toggleOff(parent)) {
      const text = document.createTextNode(parent.textContent ?? '')
      parent.replaceWith(text)
      emit()
      return
    }

    const span = document.createElement('span')
    span.className = className
    try {
      range.surroundContents(span)
    } catch {
      // A selection spanning a tag boundary cannot be surrounded; extracting
      // and re-inserting is the fallback every editor uses for it.
      span.appendChild(range.extractContents())
      range.insertNode(span)
    }
    selection.removeAllRanges()
    emit()
  }

  const colorOf = (): string => {
    const parent = window.getSelection()?.anchorNode?.parentElement
    const found = NOTE_COLORS.find(
      (entry) => parent?.classList.contains(noteColorClass(entry.value)))
    return found?.value ?? 'text'
  }

  /** The palette key as a CSS colour, for the swatches and the A's bar. */
  const swatchOf = (value: string) => (value === 'text'
    ? 'rgb(var(--color-text))'
    : `rgb(var(--color-${value}))`)

  return (
    <div className={cn('rounded-field border border-line bg-surface', disabled && 'opacity-50')}>
      <div className="flex items-center gap-0.5 border-b border-line-soft px-1 py-1">
        <ToolButton label="Bold" onClick={() => run('bold')} disabled={disabled}>
          <IconBold size={15} />
        </ToolButton>
        <ToolButton label="Italic" onClick={() => run('italic')} disabled={disabled}>
          <IconItalic size={15} />
        </ToolButton>
        <ToolButton label="Bulleted list" onClick={() => run('insertUnorderedList')}
          disabled={disabled}>
          <IconBulletList size={15} />
        </ToolButton>
        <ToolButton
          label="Highlight"
          disabled={disabled}
          onClick={() => wrap(
            NOTE_MARK_CLASS, (element) => element.classList.contains(NOTE_MARK_CLASS))}
        >
          <IconHighlight size={15} />
        </ToolButton>

        <MenuSelect
          label="Text colour"
          keepSelection
          value={colorOf()}
          onChange={(next) => {
            if (!next) return
            wrap(
              noteColorClass(next),
              (element) => NOTE_COLORS.some(
                (entry) => element.classList.contains(noteColorClass(entry.value))))
          }}
          options={NOTE_COLORS.map((entry) => ({
            value: entry.value,
            label: entry.label,
            swatch: swatchOf(entry.value),
          }))}
          minWidth={160}
          className="h-6 min-w-[24px] px-1"
          disabled={disabled}
          triggerContent={(chosen) => (
            /*
             * Word's font-colour button: the A drawn in the toolbar's own
             * colour, and under it a bar in the colour it would write in. The
             * bar is laid over the icon's own baseline rather than added below
             * it, so the button stays 15px tall like its neighbours.
             */
            <span className="relative block" style={{ width: 15, height: 15 }} aria-hidden>
              <IconTextColor size={15} />
              <span
                className="absolute inset-x-[1px] bottom-[1px] h-[3px] rounded-[1px]"
                style={{ background: swatchOf(chosen?.value ?? 'text') }}
              />
            </span>
          )}
        />
      </div>

      <div className="relative">
        {empty && placeholder && (
          <span className="pointer-events-none absolute left-2 top-2 text-cell text-text-faint">
            {placeholder}
          </span>
        )}
        <div
          ref={box}
          role="textbox"
          aria-multiline="true"
          aria-label="Note"
          contentEditable={!disabled}
          suppressContentEditableWarning
          onInput={emit}
          onBlur={emit}
          onPaste={(event) => {
            /*
             * Paste as text. Pasting from a browser or a document brings a
             * page of markup with it, and while the sanitiser would strip it,
             * what lands in the box should be what the person expects to see.
             */
            event.preventDefault()
            const text = event.clipboardData.getData('text/plain')
            document.execCommand('insertText', false, text)
          }}
          style={{ minHeight }}
          className="rt w-full px-2 py-2 text-cell text-text focus:outline-none"
        />
      </div>
    </div>
  )
}

function ToolButton({
  label, onClick, disabled, children,
}: {
  label: string
  onClick: () => void
  disabled?: boolean
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      // The selection must survive the click, and a button takes focus on
      // mousedown — which collapses it before the handler ever runs.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onClick}
      className="flex h-6 min-w-[24px] items-center justify-center rounded-field px-1 text-cell text-text-muted hover:bg-line-soft hover:text-text"
    >
      {children}
    </button>
  )
}

/** A stored note, rendered. The HTML is sanitised again on the way out. */
export function NoteBody({ html, className }: { html: string; className?: string }) {
  return (
    <div
      className={cn('rt text-cell text-text', className)}
      // Sanitised on the way in by the server and again here: this is the one
      // place in the product that sets HTML from stored data, so it does not
      // get to assume anybody upstream did their job.
      dangerouslySetInnerHTML={{ __html: sanitizeNote(html) }}
    />
  )
}
